import { Snackbar, Typography, Link, Box, Button, IconButton, SvgIcon } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  TOAST_AUTO_HIDE_DURATION,
  TOAST_CLOSE_ICON,
  TOAST_CLOSE_REASONS,
  TOAST_ICON_MAP,
  TOAST_PROP_TYPES,
  resolveSeverity,
} from './toast.constants'
import { useToast } from './toast.hooks'
import { styles } from './toast.styles'

const FALLBACK_TOKENS = {
  success: {
    background: '#EBF8DD',
    border: '#528C15',
    iconBadge: '#528C15',
    shadow: '0px 2px 4px rgba(82,140,21,0.24)',
  },
  error: {
    background: '#ffe2e3',
    border: '#cd3134',
    iconBadge: '#cd3134',
    shadow: '0px 2px 4px rgba(232,32,32,0.24)',
  },
  info: {
    background: '#e6f6fc',
    border: '#818df2',
    iconBadge: '#29369c',
    shadow: '0px 1px 4px rgba(30,32,38,0.16)',
  },
}

export default function Toast({ severity, title, description, isOpen, action }) {
  const { closeToast } = useToast()
  const { t } = useTranslation('common')
  const theme = useTheme()

  const resolvedSeverity = resolveSeverity(severity)
  const tokens = theme.palette?.toast?.[resolvedSeverity] || FALLBACK_TOKENS[resolvedSeverity]
  const TypeIcon = TOAST_ICON_MAP[resolvedSeverity]

  const handleActionClick = () => {
    if (action?.onClick) {
      action.onClick()
    }
    closeToast()
  }

  const handleClose = (event, reason) => {
    if (reason === TOAST_CLOSE_REASONS.CLICKAWAY) {
      return
    }
    closeToast()
  }

  const hasDescription = Boolean(description)

  return (
    <Snackbar
      open={isOpen}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={TOAST_AUTO_HIDE_DURATION}
      sx={styles.snackbar}
    >
      <Box sx={styles.outerContainer(tokens)} role="alert" data-severity={resolvedSeverity}>
        <Box sx={styles.innerContainer(hasDescription)}>
          <Box sx={styles.leftSection(hasDescription)}>
            <Box sx={styles.iconBadge(tokens)}>
              <SvgIcon component={TypeIcon} inheritViewBox sx={styles.iconSvg} />
            </Box>
            <Box sx={styles.textColumn}>
              <Typography variant="header4" sx={styles.titleText}>
                {title}
              </Typography>
              {description && (
                <Typography variant="medium" sx={styles.descriptionText}>
                  {description}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={styles.rightSection}>
            {action && (
              <>
                {action.url ? (
                  <Link
                    href={action.url}
                    sx={styles.actionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {action.label}
                  </Link>
                ) : action.onClick ? (
                  <Button
                    data-testid={action.testId}
                    onClick={handleActionClick}
                    sx={styles.actionButton}
                    variant="text"
                    disableRipple
                  >
                    {action.label}
                  </Button>
                ) : null}
              </>
            )}
            <IconButton
              onClick={closeToast}
              sx={styles.closeButton}
              size="small"
              disableRipple
              aria-label={t('toast.closeNotification')}
            >
              <SvgIcon component={TOAST_CLOSE_ICON} inheritViewBox sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Snackbar>
  )
}

Toast.propTypes = TOAST_PROP_TYPES
